// „Link für Aushilfe“ (A-027 S4): Link ohne Anmeldung zu EINEM Termin (oder zu den Terminen eines Einsatzes).
// Dieselbe Technik wie der Kunden-Link (module_kundenlink, Seite /m/<token>) – nur mit termin_id/einsatz_id am Link.
// Gültig bis Termin-Ende + 7 Tage (änderbar), widerrufbar. Teilen: Kopieren, per Mail, am Handy „Teilen“ (z. B. WhatsApp).
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Link2, Copy, Mail, Share2, Trash2, HelpCircle, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { hilfeOeffnen } from "@/lib/useF1Help";

const datum = (iso) => (iso ? new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }) : "");

export default function AushilfeLinkDialog({ termin = null, einsatz = null, onClose }) {
  const art = termin ? "termin" : "einsatz";
  const id = termin ? termin.id : einsatz.id;
  const [links, setLinks] = useState([]);
  const [tage, setTage] = useState(7);
  const [busy, setBusy] = useState(false);
  const [mail, setMail] = useState({});   // link_id -> {an, name}
  const adresse = (l) => `${window.location.origin}/m/${l.token}`;

  const lade = useCallback(async () => {
    try { const r = await api.get(`/module-kundenlink/${art}/${id}/links`); setLinks(Array.isArray(r.data) ? r.data : []); }
    catch { setLinks([]); }
  }, [art, id]);
  useEffect(() => { lade(); }, [lade]);

  const erzeugen = async () => {
    setBusy(true);
    try {
      await api.post(`/module-kundenlink/${art}/${id}/create`, { tage_nach_ende: Number(tage) });
      toast.success("Link erstellt");
      await lade();
    } catch (err) { toast.error(err?.response?.data?.detail || "Link konnte nicht erstellt werden"); }
    finally { setBusy(false); }
  };
  const kopieren = async (l) => {
    try { await navigator.clipboard.writeText(adresse(l)); toast.success("Link kopiert"); }
    catch {
      const f = document.createElement("textarea"); f.value = adresse(l); document.body.appendChild(f); f.select();
      try { document.execCommand("copy"); toast.success("Link kopiert"); } catch { toast.error("Kopieren nicht möglich – bitte den Link markieren"); }
      f.remove();
    }
  };
  const teilen = async (l) => {
    try { await navigator.share({ title: "Ihr Einsatz", text: "Zeit, Adresse und Auftrag:", url: adresse(l) }); }
    catch (err) { if (err?.name !== "AbortError") kopieren(l); }
  };
  const perMail = async (l) => {
    const m = mail[l.id] || {};
    if (!m.an || !m.an.includes("@")) { toast.error("Bitte eine E-Mail-Adresse eintragen"); return; }
    try {
      await api.post(`/module-kundenlink/${l.id}/send-mail`, { to_email: m.an, recipient_name: m.name || "", base_url: window.location.origin });
      toast.success(`Link an ${m.an} gesendet`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Mail konnte nicht gesendet werden"); }
  };
  const widerrufen = async (l) => {
    if (!window.confirm("Link widerrufen? Er funktioniert dann sofort nicht mehr.")) return;
    try { await api.post(`/module-kundenlink/${l.id}/revoke`); toast.success("Link widerrufen"); lade(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Fehler"); }
  };
  const gueltigBis = async (l, wert) => {
    if (!wert) return;
    try { await api.post(`/module-kundenlink/${l.id}/set-expiry`, { gueltig_bis: wert }); toast.success("Gültigkeit geändert"); lade(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Fehler"); }
  };
  const aktiv = (l) => !l.revoked && new Date(l.expires_at) > new Date();
  const teilenGeht = typeof navigator !== "undefined" && typeof navigator.share === "function";

  return (
    <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4" data-testid="aushilfe-dialog">
      <div className="bg-background rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold flex items-center gap-2"><Link2 className="w-5 h-5" /> Link für Aushilfe</h2>
          <div className="flex items-center gap-1">
            <button onClick={() => hilfeOeffnen("hilfe_aushilfe_link")} className="p-1.5 hover:bg-muted rounded-sm" title="Hilfe" aria-label="Hilfe" data-testid="aushilfe-hilfe"><HelpCircle className="w-5 h-5" /></button>
            <button onClick={onClose} className="p-1.5 hover:bg-muted rounded-sm" aria-label="Schließen" data-testid="aushilfe-schliessen"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="p-4 space-y-4">
          <p className="text-sm text-muted-foreground">
            {art === "termin" ? "Der Link zeigt genau diesen Termin" : "Der Link zeigt die Termine dieses Einsatzes"}: Zeit, Kunde, Adresse mit Navigation, Telefon und Auftrag.
            Die Aushilfe braucht keine Anmeldung und kann eine Notiz und Fotos hinterlassen.
          </p>
          <div className="flex items-end gap-2 flex-wrap">
            <label className="text-sm">
              <span className="block text-xs text-muted-foreground mb-1">Gültig bis Termin-Ende plus … Tage</span>
              <input type="number" min="0" max="90" value={tage} onChange={(e) => setTage(e.target.value)} className="w-24 border rounded-sm p-2 text-sm" data-testid="aushilfe-tage" />
            </label>
            <button onClick={erzeugen} disabled={busy} className="flex items-center gap-1 px-4 py-2 bg-primary text-primary-foreground rounded-sm text-sm hover:bg-primary/90 disabled:opacity-50" data-testid="aushilfe-erzeugen">
              <Plus className="w-4 h-4" /> Link erstellen
            </button>
          </div>
          {links.length === 0 && <p className="text-sm text-muted-foreground" data-testid="aushilfe-leer">Noch kein Link.</p>}
          {links.map((l) => (
            <div key={l.id} className={`border rounded-md p-3 space-y-2 ${aktiv(l) ? "" : "opacity-60"}`} data-testid={`aushilfe-link-${l.id}`}>
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className={aktiv(l) ? "text-emerald-700 font-semibold" : "text-red-700 font-semibold"} data-testid={`aushilfe-status-${l.id}`}>
                  {l.revoked ? "Widerrufen" : aktiv(l) ? `Gültig bis ${datum(l.expires_at)}` : `Abgelaufen am ${datum(l.expires_at)}`}
                </span>
                <span className="text-muted-foreground">{l.view_count || 0}× geöffnet</span>
              </div>
              <input readOnly value={adresse(l)} onFocus={(e) => e.target.select()} className="w-full border rounded-sm p-2 text-xs bg-muted/40" data-testid={`aushilfe-url-${l.id}`} />
              {!l.revoked && (
                <>
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={() => kopieren(l)} className="flex items-center gap-1 px-3 py-1.5 border rounded-sm text-sm hover:bg-muted" data-testid={`aushilfe-kopieren-${l.id}`}><Copy className="w-4 h-4" /> Kopieren</button>
                    {teilenGeht && <button onClick={() => teilen(l)} className="flex items-center gap-1 px-3 py-1.5 border rounded-sm text-sm hover:bg-muted" data-testid={`aushilfe-teilen-${l.id}`}><Share2 className="w-4 h-4" /> Teilen</button>}
                    <button onClick={() => widerrufen(l)} className="flex items-center gap-1 px-3 py-1.5 border border-red-200 text-red-700 rounded-sm text-sm hover:bg-red-50 ml-auto" data-testid={`aushilfe-widerrufen-${l.id}`}><Trash2 className="w-4 h-4" /> Widerrufen</button>
                  </div>
                  <div className="flex gap-2 flex-wrap items-center">
                    <input type="email" placeholder="E-Mail der Aushilfe" value={(mail[l.id] || {}).an || ""} onChange={(e) => setMail((m) => ({ ...m, [l.id]: { ...(m[l.id] || {}), an: e.target.value } }))}
                      className="flex-1 min-w-[10rem] border rounded-sm p-2 text-sm" data-testid={`aushilfe-mail-an-${l.id}`} />
                    <button onClick={() => perMail(l)} className="flex items-center gap-1 px-3 py-1.5 bg-slate-700 text-white rounded-sm text-sm hover:bg-slate-800" data-testid={`aushilfe-mail-senden-${l.id}`}><Mail className="w-4 h-4" /> Per Mail</button>
                  </div>
                  <label className="text-xs text-muted-foreground flex items-center gap-2">Gültig bis ändern:
                    <input type="date" onChange={(e) => gueltigBis(l, e.target.value)} className="border rounded-sm p-1 text-sm text-foreground" data-testid={`aushilfe-datum-${l.id}`} />
                  </label>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
